import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ChevronDown, Phone, Video, MoreVertical, Search, Eraser, Image as ImageIcon, BellOff, Settings as SettingsIcon, Trash2, Hash, Users, Bookmark, MessageSquare, Mic2, Eye, EyeOff, ShieldAlert, Timer, Check, X } from "lucide-react";
import type { Chat, Message, MessageKind, MediaInfo, Geo, ServerEvent } from "@glass/shared";
import { Avatar } from "./Avatar";
import { MessageBubble } from "./MessageBubble";
import { Composer } from "./Composer";
import { Modal } from "./Modal";
import { JitsiCall } from "./JitsiCall";
import { AudioRoom as AudioRoomView } from "./AudioRoom";
import { ChatSocket } from "../lib/ws";
import { api } from "../lib/api";
import { sounds } from "../lib/sounds";
import { useStore, store } from "../lib/store";

interface ChatViewProps {
  chatId: string;
  onClose?: () => void;
}

function formatDay(d: Date): string {
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  const isYest = d.toDateString() === yest.toDateString();
  if (isToday) return "Сегодня";
  if (isYest) return "Вчера";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

export function ChatView({ chatId, onClose }: ChatViewProps) {
  const me = useStore((s) => s.user);
  const settings = useStore((s) => s.settings);
  const messages = useStore((s) => s.messagesByChat[chatId] || []);
  const allChats = useStore((s) => s.chats);
  const chat: Chat | undefined = useMemo(() => allChats.find((c) => c.id === chatId), [allChats, chatId]);

  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [editing, setEditing] = useState<Message | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [floatingDate, setFloatingDate] = useState<string>("");
  const [scrollPinned, setScrollPinned] = useState(true);
  const [callOpen, setCallOpen] = useState<"video" | "audio" | null>(null);
  const [audioRoomId, setAudioRoomId] = useState<string | null>(null);
  const [wallpaperOpen, setWallpaperOpen] = useState(false);
  const [disappearingOpen, setDisappearingOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [adminLogOpen, setAdminLogOpen] = useState(false);
  const [forwardTarget, setForwardTarget] = useState<Message | null>(null);
  const wsRef = useRef<ChatSocket | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [typing, setTyping] = useState<Record<string, string>>({}); // user_id -> name
  const stealth = me?.stealth ?? false;

  // Load history on chat change
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await api.loadMessages(chatId);
        if (!cancelled) {
          store.set((s) => ({
            messagesByChat: { ...s.messagesByChat, [chatId]: r.messages },
          }));
          setTimeout(() => scrollToBottom("auto"), 50);
        }
      } catch { /* empty */ }
    })();
    return () => { cancelled = true; };
  }, [chatId]);

  // Active audio room (Spaces) discovery
  useEffect(() => {
    void (async () => {
      try {
        const r = await api.activeAudio(chatId);
        setAudioRoomId(r.room?.id || null);
      } catch { /* empty */ }
    })();
  }, [chatId]);

  // WebSocket
  useEffect(() => {
    const ws = new ChatSocket("chat", chatId);
    wsRef.current = ws;
    const off = ws.on((e: ServerEvent) => {
      if (e.type === "message_new") {
        store.appendMessage(chatId, e.message);
        if (e.message.author_id !== me?.id) {
          sounds.receive(settings?.receive_sound !== false);
        }
        if (scrollPinned) requestAnimationFrame(() => scrollToBottom("smooth"));
      } else if (e.type === "message_edited") {
        store.patchMessage(chatId, e.id, { body: e.body, edited_at: e.edited_at });
      } else if (e.type === "message_deleted") {
        if (e.for_everyone) store.removeMessage(chatId, e.id);
        else store.removeMessage(chatId, e.id);
      } else if (e.type === "reaction_changed") {
        store.patchMessage(chatId, e.message_id, { reactions: e.reactions });
      } else if (e.type === "read_receipt") {
        const list = store.get().messagesByChat[chatId] || [];
        const updated = list.map((m) => (
          new Date(m.created_at).getTime() <= new Date(e.up_to).getTime()
            ? { ...m, read_by: Array.from(new Set([...(m.read_by || []), e.user_id])) }
            : m
        ));
        store.set((s) => ({ messagesByChat: { ...s.messagesByChat, [chatId]: updated } }));
      } else if (e.type === "typing") {
        if (e.user_id !== me?.id) {
          setTyping((t) => ({ ...t, [e.user_id]: e.user_name }));
          setTimeout(() => setTyping((t) => { const c = { ...t }; delete c[e.user_id]; return c; }), 4000);
        }
      } else if (e.type === "pin_changed" && chat) {
        // soft re-fetch
        void api.getChat(chatId).then((r) => {
          store.set((s) => ({ chats: s.chats.map((c) => (c.id === chatId ? { ...c, ...r.chat } : c)) }));
        });
      } else if (e.type === "chat_settings") {
        void api.getChat(chatId).then((r) => {
          store.set((s) => ({ chats: s.chats.map((c) => (c.id === chatId ? { ...c, ...r.chat } : c)) }));
        });
      }
    });
    ws.connect();
    return () => { off(); ws.close(); };
  }, [chatId, me?.id, settings?.receive_sound, scrollPinned, chat]);

  // Send a "read" receipt when scrolled to bottom (debounced)
  useEffect(() => {
    if (!scrollPinned || messages.length === 0) return;
    const t = setTimeout(() => {
      wsRef.current?.send({ type: "read", up_to: messages[messages.length - 1].created_at });
    }, 600);
    return () => clearTimeout(t);
  }, [messages, scrollPinned]);

  function scrollToBottom(behavior: ScrollBehavior = "smooth") {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setScrollPinned(distFromBottom < 80);

    // Compute floating date from topmost visible message
    const items = el.querySelectorAll<HTMLElement>("[data-message-day]");
    for (const it of Array.from(items)) {
      const r = it.getBoundingClientRect();
      const top = r.top - el.getBoundingClientRect().top;
      if (top + r.height > 8) {
        setFloatingDate(it.dataset.messageDay || "");
        break;
      }
    }
  }

  async function send(kind: MessageKind, body: string, media?: MediaInfo, extra?: { poll?: { question: string; options: string[] }; geo?: Geo }) {
    wsRef.current?.send({
      type: "send",
      kind, body, media, reply_to: replyTo?.id, ...extra,
    });
    sounds.send(settings?.send_sound !== false);
    setReplyTo(null);
  }

  function react(m: Message, emoji: string) {
    wsRef.current?.send({ type: "react", id: m.id, emoji });
  }

  function deleteMsg(m: Message, forEveryone: boolean) {
    wsRef.current?.send({ type: "delete", id: m.id, for_everyone: forEveryone });
  }

  function pin(m: Message) {
    wsRef.current?.send({ type: "pin", id: m.id });
  }

  function saveEdit(newBody: string) {
    if (!editing) return;
    wsRef.current?.send({ type: "edit", id: editing.id, body: newBody });
    setEditing(null);
  }

  function jumpTo(id: string) {
    document.querySelector<HTMLElement>(`[data-message-id="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // Group messages by day with separators
  const grouped = useMemo(() => {
    const out: Array<{ day: string; messages: Message[] }> = [];
    for (const m of messages) {
      const day = formatDay(new Date(m.created_at));
      const last = out[out.length - 1];
      if (!last || last.day !== day) out.push({ day, messages: [m] });
      else last.messages.push(m);
    }
    return out;
  }, [messages]);

  if (!chat) return <div className="grid place-items-center w-full h-full text-ink-300">Загрузка чата…</div>;

  const chatTypeIcon = chat.type === "channel" ? <Hash size={14} /> :
    chat.type === "group" || chat.type === "community" ? <Users size={14} /> :
    chat.type === "audio_room" ? <Mic2 size={14} /> :
    chat.type === "saved" ? <Bookmark size={14} /> : <MessageSquare size={14} />;

  return (
    <main className="flex-1 h-dvh flex flex-col relative" style={chat.wallpaper_url ? { backgroundImage: `url(${chat.wallpaper_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}>
      {chat.wallpaper_url && <div className="absolute inset-0" style={{ background: "rgba(6,8,13,0.6)", backdropFilter: "blur(4px)" }} />}

      <header className="relative z-10 glass-strong rounded-none px-4 py-3 flex items-center gap-3 border-b border-white/10">
        {onClose && <button className="md:hidden btn btn-ghost p-2 rounded-full" onClick={onClose}><X size={16} /></button>}
        <Avatar name={chat.title} url={chat.avatar_url} size={36} />
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate flex items-center gap-1.5">{chat.title}{chat.muted && <BellOff size={12} className="text-ink-400" />}</div>
          <div className="text-xs text-ink-300 truncate flex items-center gap-1.5">
            {chatTypeIcon}
            {Object.values(typing).length > 0
              ? <span className="text-accent-300">{Object.values(typing).join(", ")} печатает…</span>
              : chat.member_count > 1
                ? `${chat.member_count} участников`
                : chat.username ? `@${chat.username}` : "Личный чат"}
          </div>
        </div>
        {audioRoomId && (
          <button className="btn btn-primary px-3" title="Перейти в Audio Room" onClick={() => setCallOpen("audio")}><Mic2 size={14} /> Live</button>
        )}
        <button className="btn btn-ghost p-2 rounded-full" title="Голосовой звонок" onClick={() => setCallOpen("audio")}><Phone size={16} /></button>
        <button className="btn btn-ghost p-2 rounded-full" title="Видеозвонок" onClick={() => setCallOpen("video")}><Video size={16} /></button>
        <button className="btn btn-ghost p-2 rounded-full" title="Поиск" onClick={() => setSearchQuery(searchQuery === null ? "" : null)}><Search size={16} /></button>
        <div className="relative">
          <button className="btn btn-ghost p-2 rounded-full" onClick={() => setShowMenu((v) => !v)}><MoreVertical size={16} /></button>
          {showMenu && (
            <div className="absolute right-0 top-11 z-30 glass-strong p-1 rounded-xl text-sm min-w-[220px]">
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10" onClick={() => { setShowMenu(false); api.clearHistory(chatId).catch(() => 0); }}><Eraser size={14} /> Очистить чат</button>
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10" onClick={() => { setShowMenu(false); setWallpaperOpen(true); }}><ImageIcon size={14} /> Сменить обои</button>
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10" onClick={() => { setShowMenu(false); setSearchQuery(searchQuery === null ? "" : null); }}><Search size={14} /> Поиск в чате</button>
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10" onClick={() => { setShowMenu(false); api.muteChat(chatId, !chat.muted).then(() => api.listChats().then(r => store.set({ chats: r.chats }))); }}><BellOff size={14} /> {chat.muted ? "Включить уведомления" : "Без звука"}</button>
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10" onClick={() => { setShowMenu(false); setDisappearingOpen(true); }}><Timer size={14} /> Исчезающие сообщения</button>
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10" onClick={() => { setShowMenu(false); setAdminLogOpen(true); }}><ShieldAlert size={14} /> Журнал админа</button>
              {!audioRoomId && (chat.type === "group" || chat.type === "community" || chat.type === "audio_room") && (
                <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10" onClick={async () => {
                  setShowMenu(false);
                  const r = await api.startAudio(chatId, "Live Audio");
                  setAudioRoomId(r.room_id);
                  setCallOpen("audio");
                }}><Mic2 size={14} /> Начать Audio Room</button>
              )}
              <div className="my-1 h-px bg-white/10" />
              <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-500/15 text-red-300" onClick={async () => { setShowMenu(false); if (confirm(`Удалить чат «${chat.title}»?`)) { await api.leaveChat(chatId); api.listChats().then(r => store.set({ chats: r.chats })); onClose?.(); } }}><Trash2 size={14} /> Удалить чат</button>
            </div>
          )}
        </div>
      </header>

      {searchQuery !== null && (
        <div className="relative z-10 glass-soft px-4 py-2 flex items-center gap-2">
          <Search size={14} className="text-ink-400" />
          <input className="bg-transparent flex-1 outline-none" autoFocus placeholder="Искать в этом чате…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          <button className="btn btn-ghost p-1.5 rounded-full" onClick={() => setSearchQuery(null)}><X size={14} /></button>
        </div>
      )}

      <div ref={scrollRef} onScroll={onScroll}
        className="relative flex-1 overflow-y-auto px-3 md:px-6 py-3 flex flex-col gap-1.5 z-0">
        {/* Top shadow */}
        <div className="pointer-events-none absolute top-0 left-0 right-0 h-12 scroll-shadow-top z-[1]" />
        {/* Bottom shadow */}
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 scroll-shadow-bottom z-[1]" />

        {/* Floating date pill */}
        {floatingDate && (
          <div className="floating-date pill text-xs">{floatingDate}</div>
        )}

        {grouped.length === 0 && (
          <div className="grid place-items-center h-full text-ink-300 text-sm">Сообщений пока нет — начните разговор</div>
        )}

        {grouped.map((g) => (
          <div key={g.day} className="contents">
            <div className="self-center pill text-[11px] my-2" data-message-day={g.day}>{g.day}</div>
            {g.messages
              .filter((m) => !searchQuery || (m.body || "").toLowerCase().includes(searchQuery.toLowerCase()))
              .map((m, i, arr) => {
                const isMe = m.author_id === me?.id;
                const prev = arr[i - 1];
                const showAvatar = !prev || prev.author_id !== m.author_id;
                return (
                  <div key={m.id} data-message-id={m.id} className="flex">
                    <MessageBubble msg={m} isMe={isMe} showAvatar={showAvatar}
                      authorName={isMe ? me?.name || "Я" : m.author_name || "Гость"}
                      authorAvatar={null}
                      onReply={(mm) => setReplyTo(mm)}
                      onReact={(mm, e) => react(mm, e)}
                      onEdit={(mm) => setEditing(mm)}
                      onDelete={(mm, fe) => deleteMsg(mm, fe)}
                      onForward={(mm) => setForwardTarget(mm)}
                      onPin={(mm) => pin(mm)}
                      onJumpTo={jumpTo} />
                  </div>
                );
              })}
          </div>
        ))}
      </div>

      {!scrollPinned && (
        <button className="absolute bottom-28 right-6 z-20 btn btn-primary p-2.5 rounded-full shadow-floating" title="Вниз" onClick={() => scrollToBottom("smooth")}>
          <ArrowDown size={18} />
        </button>
      )}

      {stealth && (
        <div className="self-center absolute top-20 z-10 pill text-[11px]"><EyeOff size={12} /> Stealth: онлайн скрыт</div>
      )}

      <div className="relative z-10">
        <Composer
          onSend={(kind, body, media, extra) => send(kind, body, media, extra)}
          replyTo={replyTo} cancelReply={() => setReplyTo(null)}
          editing={editing} saveEdit={saveEdit} cancelEdit={() => setEditing(null)}
          sendSoundEnabled={settings?.send_sound !== false} />
      </div>

      {callOpen && callOpen !== "audio" && (
        <JitsiCall chatId={chatId} mode={callOpen} onClose={() => setCallOpen(null)} />
      )}
      {callOpen === "audio" && audioRoomId && (
        <AudioRoomView roomId={audioRoomId} chatId={chatId} onClose={() => setCallOpen(null)} />
      )}
      {callOpen === "audio" && !audioRoomId && (
        <JitsiCall chatId={chatId} mode="audio" onClose={() => setCallOpen(null)} />
      )}

      <Modal open={wallpaperOpen} onClose={() => setWallpaperOpen(false)} title="Обои чата">
        <WallpaperPicker chatId={chatId} current={chat.wallpaper_url || null} onPicked={async (url) => {
          await api.setWallpaper(chatId, url);
          await api.getChat(chatId).then((r) => store.set((s) => ({ chats: s.chats.map((c) => (c.id === chatId ? { ...c, wallpaper_url: r.chat.wallpaper_url } : c)) })));
          setWallpaperOpen(false);
        }} />
      </Modal>

      <Modal open={disappearingOpen} onClose={() => setDisappearingOpen(false)} title="Исчезающие сообщения">
        <DisappearingPicker chatId={chatId} current={chat.disappearing_seconds} onPicked={async (s) => {
          await api.setDisappearing(chatId, s);
          await api.getChat(chatId).then((r) => store.set((st) => ({ chats: st.chats.map((c) => (c.id === chatId ? { ...c, disappearing_seconds: r.chat.disappearing_seconds } : c)) })));
          setDisappearingOpen(false);
        }} />
      </Modal>

      <Modal open={adminLogOpen} onClose={() => setAdminLogOpen(false)} title="Журнал админа">
        <AdminLog chatId={chatId} />
      </Modal>

      <Modal open={!!forwardTarget} onClose={() => setForwardTarget(null)} title="Переслать">
        {forwardTarget && (
          <ForwardPicker exceptChatId={chatId} onForward={(targetChatId) => {
            const t = forwardTarget;
            api.request<{ ok: true }>(`/api/chats/${targetChatId}/messages`, {
              method: "POST",
              body: JSON.stringify({ forward_from: { chat_id: chatId, message_id: t.id, body: t.body, kind: t.kind, media: t.media } }),
            }).catch(() => 0);
            setForwardTarget(null);
          }} />
        )}
      </Modal>
    </main>
  );
}

function WallpaperPicker({ chatId: _chatId, current, onPicked }: { chatId: string; current: string | null; onPicked: (url: string | null) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const presets = [
    null,
    "https://images.unsplash.com/photo-1487744480471-9ca1bca6fb7d?w=1600&q=80",
    "https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=1600&q=80",
    "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1600&q=80",
    "https://images.unsplash.com/photo-1540206351-d6465b3ac5c1?w=1600&q=80",
  ];
  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        {presets.map((u, i) => (
          <button key={i} className={`relative rounded-xl overflow-hidden h-24 border-2 ${current === u ? "border-accent-400" : "border-white/10"}`} onClick={() => onPicked(u)}>
            {u ? <img src={u} className="w-full h-full object-cover" alt="" /> : <span className="grid place-items-center w-full h-full text-xs text-ink-300">Без обоев</span>}
            {current === u && <Check size={14} className="absolute top-1 right-1 text-accent-300" />}
          </button>
        ))}
      </div>
      <div className="mt-3">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
          const f = e.target.files?.[0]; if (!f) return;
          const up = await api.uploadMedia(f, f.type);
          onPicked(up.url);
        }} />
        <button className="btn btn-ghost w-full" onClick={() => fileRef.current?.click()}>Загрузить свои…</button>
      </div>
    </div>
  );
}

function DisappearingPicker({ chatId: _chatId, current, onPicked }: { chatId: string; current: number | null; onPicked: (s: number | null) => void }) {
  const opts: Array<[string, number | null]> = [
    ["Выкл", null], ["1 час", 3600], ["6 часов", 21600], ["1 день", 86400], ["3 дня", 259200], ["7 дней", 604800],
  ];
  return (
    <div className="grid grid-cols-3 gap-2">
      {opts.map(([label, s]) => (
        <button key={label} className={`btn ${current === s ? "btn-primary" : "btn-ghost"}`} onClick={() => onPicked(s)}>{label}</button>
      ))}
    </div>
  );
}

function AdminLog({ chatId }: { chatId: string }) {
  const [items, setItems] = useState<Array<{ id: string; ts: string; actor_id: string; action: string; meta: string }>>([]);
  useEffect(() => {
    void (async () => {
      const r = await api.request<{ entries: Array<{ id: string; ts: string; actor_id: string; action: string; meta: string }> }>(`/api/chats/${chatId}/admin-log`);
      setItems(r.entries);
    })();
  }, [chatId]);
  if (items.length === 0) return <div className="text-sm text-ink-400">Записей нет.</div>;
  return (
    <div className="space-y-1.5 max-h-96 overflow-y-auto">
      {items.map((e) => (
        <div key={e.id} className="text-sm glass-row p-2">
          <div className="text-xs text-ink-400">{new Date(e.ts).toLocaleString()}</div>
          <div className="font-mono text-xs"><b>{e.action}</b> · {e.actor_id} · {e.meta}</div>
        </div>
      ))}
    </div>
  );
}

function ForwardPicker({ exceptChatId, onForward }: { exceptChatId: string; onForward: (chatId: string) => void }) {
  const chats = useStore((s) => s.chats).filter((c) => c.id !== exceptChatId);
  return (
    <div className="space-y-1.5 max-h-96 overflow-y-auto">
      {chats.map((c) => (
        <button key={c.id} className="w-full glass-row p-2 flex items-center gap-3 text-left" onClick={() => onForward(c.id)}>
          <Avatar name={c.title} url={c.avatar_url} size={32} />
          <div className="font-medium truncate">{c.title}</div>
        </button>
      ))}
    </div>
  );
}
